// Turning a saved layout into a file someone can send a colleague, and a file they were sent back
// into something importable. Pure, and kept apart from grid-layout-state.js: that module is about the
// shape of a DevExtreme grid state, this one is about the shape of the file that carries it.
//
// There are no shared layouts in Squire — every layout belongs to the user who saved it — so a .txt
// file IS the sharing mechanism, and it has to survive being emailed, opened in Notepad and saved
// again. Hence readable indented JSON rather than anything encoded.

import { sanitizeLayoutStateForApply, parseLayoutData } from './grid-layout-state';

// A discriminator, so a file that happens to be JSON is refused before it reaches the server rather
// than being stored as a layout that will never apply to anything.
export const LAYOUT_FILE_TYPE = 'squire.gridLayout';

// The envelope's own version, NOT the layout state's SchemaVersion (which comes from the domain and
// rides along inside). Bump this only if the envelope's fields change.
export const LAYOUT_FILE_VERSION = 1;

export const LAYOUT_FILE_EXTENSION = '.txt';

const FILE_NAME_FALLBACK = 'grid-layout';
const FILE_NAME_PART_MAX_LENGTH = 80;

// Windows refuses \ / : * ? " < > | in a file name and macOS refuses :, so the union goes.
const ILLEGAL_FILE_NAME_CHARACTERS = /[\\/:*?"<>|]/g;

const NOT_A_LAYOUT_FILE = 'That file is not a Squire grid layout file.';

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asTrimmedString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

// Spaces are left alone — they are legal in a file name and they read better. Runs of replacements
// collapse to one dash: a layout named 'Q1 "jobs": open' would otherwise come out as -jobs--.
function toFileNamePart(value) {
  const cleaned = String(value ?? '')
    .replace(ILLEGAL_FILE_NAME_CHARACTERS, '-')
    .replace(/\s+/g, ' ')
    .replace(/-{2,}/g, '-')
    .trim()
    .slice(0, FILE_NAME_PART_MAX_LENGTH)
    .trim();

  // Nothing but separators left (a layout named '///') is not a file name anyone can tell apart.
  if (cleaned === '' || /^[-.\s]*$/.test(cleaned)) {
    return FILE_NAME_FALLBACK;
  }

  return cleaned;
}

function toSchemaVersion(value) {
  const schemaVersion = Number(value);
  return Number.isInteger(schemaVersion) && schemaVersion > 0 ? schemaVersion : 1;
}

/**
 * Builds the file for one saved layout.
 *
 * Takes the layout as `getGridLayout` returns it. `id` and `isDefault` are deliberately left out of
 * the envelope: the id means nothing in the recipient's account, and whether the sender made it their
 * default is their preference, not part of the layout.
 *
 * @param {{layoutKey: string, name: string, data: string|object, schemaVersion?: number}} layout
 * @returns {{fileName: string, contents: string}}
 */
export function buildLayoutExport(layout) {
  const state = parseLayoutData(layout?.data);
  if (!state) {
    throw new Error('There is no layout data to export.');
  }

  const layoutKey = asTrimmedString(layout.layoutKey);
  const name = asTrimmedString(layout.name);

  const envelope = {
    fileType: LAYOUT_FILE_TYPE,
    fileVersion: LAYOUT_FILE_VERSION,
    layoutKey,
    name,
    schemaVersion: toSchemaVersion(layout.schemaVersion),
    // Embedded as an object rather than a nested JSON string: the file is meant to be readable, and
    // a string of escaped JSON inside JSON is not.
    data: state,
  };

  return {
    fileName: `${toFileNamePart(layoutKey)} - ${toFileNamePart(name)}${LAYOUT_FILE_EXTENSION}`,
    contents: `${JSON.stringify(envelope, null, 2)}\n`,
  };
}

/**
 * Reads a file the user picked. Throws an Error whose message is fit to show them — every caller
 * routes it straight to a notification.
 *
 * The returned `data` is a JSON **string**, which is what the import endpoint takes.
 *
 * @param {string} text
 * @returns {{layoutKey: string, name: string, data: string, schemaVersion: number}}
 */
export function parseLayoutImport(text) {
  let envelope;
  try {
    envelope = JSON.parse(text);
  } catch {
    throw new Error(NOT_A_LAYOUT_FILE);
  }

  if (!isPlainObject(envelope) || envelope.fileType !== LAYOUT_FILE_TYPE) {
    throw new Error(NOT_A_LAYOUT_FILE);
  }

  const fileVersion = Number(envelope.fileVersion);
  if (!Number.isFinite(fileVersion)) {
    throw new Error(NOT_A_LAYOUT_FILE);
  }

  if (fileVersion > LAYOUT_FILE_VERSION) {
    throw new Error(
      'That layout file was written by a newer version of Squire and cannot be imported.'
    );
  }

  const layoutKey = asTrimmedString(envelope.layoutKey);
  if (!layoutKey) {
    throw new Error('That layout file does not say which grid it came from.');
  }

  const name = asTrimmedString(envelope.name);
  if (!name) {
    throw new Error('That layout file does not have a layout name.');
  }

  // Strips what must never be persisted (a selected row, a page index) from a file that may have been
  // written by an older build or edited by hand. No known fields are passed, so columns are left
  // alone — the grid gets its say when the layout is applied.
  const { state } = sanitizeLayoutStateForApply(envelope.data);
  if (!state) {
    throw new Error('That layout file does not contain any layout data.');
  }

  return {
    layoutKey,
    name,
    data: JSON.stringify(state),
    schemaVersion: toSchemaVersion(envelope.schemaVersion),
  };
}

/**
 * Whether a file's layout key is for the grid being imported into.
 *
 * Only a fast client-side check so a mismatch is refused without a round trip — the refusal that
 * counts is the server's, in ImportGridLayoutCommand. It compares trimmed and case-insensitively
 * (the database collation is case-insensitive too), so this has to agree or the client would refuse
 * files the server would take.
 */
export function isMatchingLayoutKey(left, right) {
  const target = asTrimmedString(left);
  const other = asTrimmedString(right);

  if (!target || !other) {
    return false;
  }

  return target.toLowerCase() === other.toLowerCase();
}
